import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { ResponseAnswer } from '../forum/types/helpers/response.answer.model';
import { RequestDetails } from '../forum/types/request-details.model';
import { PromoCategory } from '../forum/types/promo-category.model';
import { Request } from './types/request.model';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../public/auth.service';
import { APP_SOURCE } from '../shared/types/app-source.enum';
import { EVENT_INTEREST_VALUE } from '../shared/types/app-interest-event.enum';

@Injectable({
    providedIn: 'root',
})
export class RequestService {
    // Hardcoded user object
    totalAnswers: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    totalProposals: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    constructor(private http: HttpClient, private toastr: ToastrService, private authService: AuthService) { }

    /**
     * Loads requests (list)
     * @param offset start of item number
     * @param limit number of items to load
     * @param historic history is true/false
     */
    getRequests(offset: number = 0, limit: number = 15, searchString: string = '', category: string = '', requestTypeFilter: string, isHistoric: boolean = false) {
        let url = `${environment.domain}${environment.api.request.requests}?limit=${limit}&offset=${offset}&historic=${isHistoric}`;
        if (!this.authService.hasToken()) {
            url = `${environment.domain}${environment.api.public.request}?limit=${limit}&offset=${offset}&historic=true`;
        }

        if (category && category !== '0') {
            url = `${url}&category=${category}`;
        }

        if (requestTypeFilter ) {
            url = `${url}&request_type=${requestTypeFilter}`;
        }

        if (searchString) {
            url = `${url}&title=${searchString}`;
        }

        return this.http.get(url).pipe(
            map((response: HttpResponse<any>) => {
                return (<any>response);
            })
        );
    }

    /**
     * Loads request detail
     * @param slug slug of request
     */
    getRequestDetails(slug: string): Observable<Request> {
        let url = `${environment.domain}${environment.api.public.request}${slug}?response_type=full`;
        if (this.authService.hasToken()) {
            url = `${environment.domain}${environment.api.request.requests}${slug}?response_type=full`;
        }
        return this.http.get(url).pipe(
            map((response: HttpResponse<any>): Request => {
                return (<any>response);
            })
        );
    }

    /**
     * Loads comments for a request
     * @param string slug
     * @param number limit
     * @param number offset
     */
    getComments(slug: string, limit: number, offset: number): Observable<Request> {
        let url = `${environment.domain}${environment.api.request.loggedOutComment}?request=${slug}&limit=${limit}&offset=${offset}`;
        if (this.authService.hasToken()) {
            url = `${environment.domain}${environment.api.request.loggedInComment}?request=${slug}&limit=${limit}&offset=${offset}`;
        } else {
            // TODO: Remove this later, for now sending back blank observable
            return of(null);
        }
        return this.http.get(url).pipe(
            map((response: HttpResponse<any>): Request => {
                return (<any>response);
            })
        );
    }

    /**
     * Loads promotional categories
     */
    getPromoCategories(): Observable<PromoCategory[]> {
        return of([
            {
                "slug": "internet-startups",
                "value": "Internet Startups"
            },
            {
                "slug": "technology-events",
                "value": "Current Events in Technology"
            },
            {
                "slug": "www",
                "value": "World Wide Web"
            },
            {
                "slug": "smartphones",
                "value": "Smart Phones"
            },
            {
                "slug": "internet",
                "value": "The Internet"
            },
            {
                "slug": "tech-trends",
                "value": "Technology Trends"
            }
        ]);
    }


    /**
     * Posts new question to requests
     */
    postNewQuestion(question:any) {
        let newQuestion = {
            ...question
        };

        const url = `${environment.domain}${environment.api.request.requests}`;
        return this.http.post(url, newQuestion);
    }

    /**
    * User is curious about request
    */
    expressCuriousInterest(req: Request) {
        let url = `${environment.domain}${environment.api.request.expressInterest}`;
        return this.http.post(url, {
            request: req.id,
            event_type: EVENT_INTEREST_VALUE.CURIOUS,
            source: APP_SOURCE.WEBSITE
        });
    }

    /**
     * User has shown interest in the request
     */
    expressInterest(req: Request) {
        let url = `${environment.domain}${environment.api.request.expressInterest}`;
        return this.http.post(url, {
            request: req.id,
            event_type: EVENT_INTEREST_VALUE.INTERESTED,
            source: APP_SOURCE.WEBSITE
        });
    }

    /**
     * User resets interest
     */
    removeInterest(req: Request) {
        let url = `${environment.domain}${environment.api.request.expressInterest}`;
        return this.http.post(url, {
            request: req.id,
            event_type: EVENT_INTEREST_VALUE.RESET_INTEREST,
            source: APP_SOURCE.WEBSITE
        });
    }

    /**
     * Response answers for a request
     */
    getResponseAnswers(requestId: number, offset: number, limit: number): Observable<ResponseAnswer> {
        const url = `${environment.domain}${environment.api.request.responseAnswers}?response_type=full&request=${requestId}&limit=${limit}&offset=${offset}`;
        return this.http.get(url, { observe: 'response' }).pipe(
            tap((response: HttpResponse<any>) => {
                this.totalAnswers.next(<number>((<any>response).count));
            }),
            map((response: HttpResponse<any>): ResponseAnswer => {
                return (<any>response.body);
            })
        );
    }

    /**
     * Adds a new comment to the request
     */
    postNewComment(comment: string, req: Request) {
        let newComment = {
            request: req.id,
            comment: comment
        };
        const url = `${environment.domain}${environment.api.request.loggedInComment}`;
        return this.http.post(url, newComment);
    }

    /**
     * Add reaction on request
     */
    addReaction(id: number, reactionType: number) {
        const data = {
            request: id,
            reaction_type: reactionType
        };
        let url = `${environment.domain}${environment.api.request.reaction}`;
        return this.http.post(url, data);
    }

    /**
     * Update reaction on request
     */
    updateReaction(id: number, reactionType: number, reactionUuid: string) {
        const data = {
            request: id,
            reaction_type: reactionType
        };
        let url = `${environment.domain}${environment.api.request.reaction}${reactionUuid}/`;
        return this.http.put(url, data);
    }

    /**
     * Delete reaction
     */
    deleteReaction(reactionUuid: string) {
        let url = `${environment.domain}${environment.api.request.reaction}${reactionUuid}`;
        return this.http.delete(url);
    }

    /**
     * Upvotes the item
     */
    postNewVote(req: Request | RequestDetails, value: number) {
        let vote = {
            request: req.id,
            vote: value
        };
        const url = `${environment.domain}${environment.api.request.vote}`;
        return this.http.post(url, vote);
    }

    /**
     * Deletes the vote
     */
    removeVote(req: Request | RequestDetails) {
        const url = `${environment.domain}${environment.api.request.vote}${req.voted.uuid}`;
        return this.http.delete(url);
    }

    /**
     * Update vote
     */
    updateVote(req: Request | RequestDetails, value: number, voteUuid: string) {
        let vote = {
            request: req.id,
            vote: value
        };
        const url = `${environment.domain}${environment.api.request.vote}${voteUuid}/`;
        return this.http.put(url, vote);
    }

    /**
     * Added new reaction
     */
    onReactionUpdated(ev, selectedRequest: Request) {
        if (!selectedRequest.reacted) {
            this.addReaction(selectedRequest.id, ev.value).subscribe((response: any) => {
                selectedRequest.reacted = response;
            }, (err) => {
                this.toastr.error('Error occurred while adding the reaction, please try again later...')
            })
        }

        if (selectedRequest.reacted && ev.value === selectedRequest.reacted.reaction_type) {
            this.deleteReaction(selectedRequest.reacted.uuid).subscribe((response: any) => {
                selectedRequest.reacted = null;
            }, (err) => {
                this.toastr.error('Error occurred while removing the reaction, please try again later...')
            })
        }

        if (selectedRequest.reacted && ev.value !== selectedRequest.reacted.reaction_type) {
            this.updateReaction(selectedRequest.id, ev.value, selectedRequest.reacted.uuid).subscribe((response: any) => {
                selectedRequest.reacted = response;
            }, (err) => {
                this.toastr.error('Error occurred while updating the reaction, please try again later...')
            })
        }
    }

    /**
     * Post a new comment
     */
    onCommentAdded(comment: string, req: Request) {
        this.postNewComment(comment, req).subscribe((response: any) => {
            let user = JSON.parse(localStorage.getItem('user'));
            response.user = {
                name: `${user.first_name} ${user.last_name}`,
                professional_title: `${user.credentials.professional_title}`
            };
            if (!req.comments) {
                req.comments = [];
            }
            req.comments.push(response);
        }, (err) => {
        });
    }

    /**
     * Add to favorites
     */
    addToFavorites(req: Request) {
        const url = `${environment.domain}${environment.api.request.save}`;
        return this.http.post(url, { request: req.id });
    }

    /**
     * Remove from favorites
     */
    removeFromFavorites(uuid: string) {
        const url = `${environment.domain}${environment.api.request.save}${uuid}`;
        return this.http.delete(url);
    }

    /**
     * Gets similar requests
     */
    getSimilarRequests(slug: string): Observable<Request[]> {
        let url = `${environment.domain}${environment.api.request.loggedOutSimilar}?slug=${slug}`;
        if (this.authService.hasToken()) {
            url = `${environment.domain}${environment.api.request.loggedInSimilar}?slug=${slug}`;
        }
        return this.http.get(url).pipe(
            map((response: HttpResponse<any>): Request[] => {
                return (<any>response).items;
            })
        );
    }

    /**
     * Creates a request
     */
    createRequest(data: any) {
        const url = `${environment.domain}${environment.api.request.requests}`;
        return this.http.post(url, {
            body: data.body,
            request_type: data.request_type,
            category: data.category,
            country: data.country?.id ?? data.country,
            city: data.city?.id ?? data.city,
            title: data.title,
            start_date: data.start_date,
            end_date: data.end_date,
            allow_profile_visible: data.allow_profile_visible,
            tags: data.tags
        });
    }

    /**
     * Saves draft (stub)
     */
    saveRequestDraft(): Observable<any> {
        return of([]);
    }

    /**
     * Submit text response to a request
     */
    submitRequestResponse(requestId: number, response: string): Observable<any> {
        const url = `${environment.domain}${environment.api.request.response}`;
        return this.http.post(url, {
            request: requestId,
            response: response
        });
    }

    /**
     * Updates text response
     */
     updateRequestResponse(uuid: string, requestId: number, response: string): Observable<any> {
        const url = `${environment.domain}${environment.api.request.response}${uuid}/`;
        return this.http.put(url, {
            response: response,
            request: requestId,
        });
    }

}
